import { useCallback, useEffect, useMemo, useState } from 'react'
import { ModalEditComponentProps } from '../../../../../../../baseComponents/ModalsLayouts/types'
import { TwinCalendarStartEndStateFull } from '../../../../../../../baseComponents/TwinCalendar/TwinCalendarStartEnd'
import { FormRender } from '../../../../../../../forms/FormRenderer/types'
import useTwinTranslation from '../../../../../../../utils/hooks/useTwinTranslation'
import { getAllWorkingCenters, getGroups } from '../../../../../../../utils/reducers/getters'
import { EmployeeModelType } from '@teinor/erp/types/company/userInner/employee'
import { twinFetchPostJSON } from '../../../../../../../utils/globals/data'
import { ModalCreateEditMedium } from '../../../../../../../baseComponents/ModalsLayouts/ModalCreateEdit'
import { dictionaryComplexFromJsonArr, TwinDictionary } from '../../../../../../../utils/globals/dictionary'
import { changeErrorMessage } from '../../../../../../../utils/reducers/reduxDispatch'
import { OnSubmit } from '../../../../../../../forms/TwinForm/types'
import { WCalModalDuplicateDayErrorTy } from './types'
import { stringToDate } from '../../../../../../../utils/globals/date'

type ModalDuplicateWCalTurnProps = ModalEditComponentProps & {}

const ModalDuplicateWCalTurn: React.FC<ModalDuplicateWCalTurnProps> = ({ setOpened, onSubmit }) => {
    const { t } = useTwinTranslation()
    const workingCenters = useMemo(() => JSON.parse(JSON.stringify(getAllWorkingCenters())), [])
    const groups = useMemo(() => JSON.parse(JSON.stringify(getGroups())), [])
    const { handleOnChangeFields, allEmployees, handleOnChangeError, errors, selectedFields } = useModalDuplicateWCalTurnLogic({ onSubmit })
    let title = t('duplicateTurns', 'Duplicar turnos')
    let fields: FormRender<TwinDictionary> = [
        {
            cols: 1,
            elements: [
                {
                    name: 'dates',
                    component: 'Custom',
                    render: () => {
                        return (
                            <div>
                                <div className='regular18'>{t('duplicateDates', 'Fechas a duplicar')}</div>
                                <div className='border-solid border-b border-gray-D6 mt-20'>
                                    <TwinCalendarStartEndStateFull nameStart='copyDateStart' nameEnd='copyDateEnd'/>
                                </div>
                            </div>
                        )
                    }
                },
            ]
        },
        {
            cols: 2,
            className: 'duplicateWCalTurns_field',
            elements: [
                {
                    name: 'initialDate',
                    label: t('initialDateNewTurns', 'Fecha inicial de los nuevos turnos'),
                    required: true,
                    component: 'InputCalendarStateFull',
                    onChange: (value) => handleOnChangeFields(value, 'initialDate'),
                },
                {
                    name: 'duplicateType',
                    label: t('DuplicateType', 'Tipo de duplicado'),
                    required: true,
                    component: 'CustomSelect',
                    items: {
                        'respect': { name: t('respectWeekDays', 'Respetar los días de la semana')},
                        'copy': {name: t('copyInOrder', 'Copiar en orden')},
                        'recursive': { name: t('recursive', 'Recursivo')},
                    },
                    onChange: (value) => handleOnChangeFields(value, 'duplicateType'),
                },
            ]
        },
    ]
    if (selectedFields.initialDate && selectedFields.duplicateType === 'recursive') {
        const minDate = stringToDate(selectedFields.initialDate)
        fields?.[1]?.elements.push({
            name: 'finalDate',
            label: t('finalDateNewTurns', 'Fecha final de los nuevos turnos'),
            required: true,
            component: 'InputCalendarStateFull',
            onChange: (value) => handleOnChangeFields(value, 'finalDate'),
            minDate: minDate ? minDate : undefined ,
        })
    }

    fields.push(
        {
            cols: 1,
            title: t('workingCentersToDuplicate', 'Centros de trabajo a duplicar'),
            elements: [
                {
                    name: 'workingCenters',
                    component: 'MultipleCheckboxSelector',
                    subtitles: {
                        subtitleOptions: t('workingCenters', 'Centros de trabajo'),
                        subtitleSelectedOptions: t('workingCentersSelected', 'Centros de trabajo seleccionados')
                    },
                    items: workingCenters || {},
                    onChange: (value) => handleOnChangeFields(value, 'workingCenters')
                },
            ]
        },
        {
            cols: 1,
            title: t('groupstoDuplicate', 'Grupos a duplicar'),
            elements: [
                {
                    name: 'groups',
                    component: 'MultipleCheckboxSelector',
                    subtitles: {
                        subtitleOptions: t('groups', 'Grupos'),
                        subtitleSelectedOptions: t('groupsSelected', 'Grupos seleccionados')
                    },
                    items: groups || {},
                    onChange: (value) => handleOnChangeFields(value, 'groups')
                },
            ]
        },
        {
            cols: 1,
            title: t('employeesToDuplicate', 'Empleados a duplicar'),
            elements: [
                {
                    name: 'employees',
                    component: 'MultipleCheckboxSelector',
                    subtitles: {
                        subtitleOptions: t('employees', 'Empleados'),
                        subtitleSelectedOptions: t('employeesSelected', 'Empleados seleccionados')
                    },
                    items: dictionaryComplexFromJsonArr(allEmployees || {}),
                    keyName: 'fullname_short'
                },
            ]
        }
    )
    let renderThis: JSX.Element[] = []
    if (errors) {
        fields = []
        title = t('allTurnsDuplicatedWithExceptions', 'Se han duplicado los turnos correctamente a excepción de los siguentes:')
        const myErrors = JSON.parse(errors)
        for (const keyDay in myErrors) {
            const day = new Date(keyDay).toLocaleDateString()
            const dayErrors = myErrors[keyDay]
            renderThis.push(<ErrorsByDays day={day} dayErrors={dayErrors} workingCenters={workingCenters} key={keyDay}/>)
        }
    }
    return (
        <ModalCreateEditMedium setOpened={setOpened}  onSubmit={onSubmit} fields={fields} translations={{
            title,
            button: t('create', 'Crear')
        }} haveButtonPermissions={errors ? false : true} url='/api/app/workingCalendar/workingCalendarTurn/duplicateWCalTurns' className='duplicateModalWCalTurns' onChangeError={handleOnChangeError}>
            {renderThis}
        </ModalCreateEditMedium>
    )
}

interface TurnsToDuplicateFields {
    initialDate: string
    finalDate?: string
    workingCenters: number[]
    groups: number[]
    employees: number[]
    duplicateType?: 'respect' | 'copy' | 'recursive'
}
interface ModalDuplicateWCalTurnLogicProps {
    onSubmit: OnSubmit
}

const useModalDuplicateWCalTurnLogic = ({ onSubmit }: ModalDuplicateWCalTurnLogicProps) => {
    const { t } = useTwinTranslation()
    const [selectedFields, setSelectedFields] = useState<TurnsToDuplicateFields>({
        initialDate: '',
        finalDate: '',
        workingCenters: [],
        groups: [],
        employees: [],
        duplicateType: undefined
    })
    const [allEmployees, setAllEmployees] = useState<EmployeeModelType[] | null>(null)
    const [errors, setErrors] = useState<string | null>(null)
    const errorMessage = t('someDayCouldNotBeDuplicatedMsg', 'Hay días que no se han podido duplicar. Revisa la lista en la siguiente pantalla')
    
    const getEmployees = useCallback(async () => {
        const result = await twinFetchPostJSON('/api/app/employee/getAllEmployeesByWorkingCenterAndGroup', {
            WorkingCenterIds: selectedFields.workingCenters,
            GroupIds: selectedFields.groups
        })
        if (result) {
            setAllEmployees(result)
        }
    }, [selectedFields, setAllEmployees])

    const handleOnChangeFields = useCallback((value: any, field: keyof TurnsToDuplicateFields) => {
        setSelectedFields((old) => {
            const copy = JSON.parse(JSON.stringify(old))
            return { ...copy, [field]: value }
        })
    }, [setSelectedFields])

    const handleOnChangeError = useCallback(async (result: any) => {
        if (result) {
            changeErrorMessage(errorMessage)
            const responseJSON = await result.json()
            if (responseJSON.errors?.[0]?.msg) {
                setErrors(responseJSON.errors?.[0]?.msg)
            }
            onSubmit(result, {})
        }
    }, [setErrors, errorMessage, onSubmit])

    useEffect(() => {
        getEmployees()
    }, [getEmployees])

    return { handleOnChangeFields, selectedFields, allEmployees, handleOnChangeError, errors }
}

interface ErrorsByDaysProps {
    day: string
    dayErrors: WCalModalDuplicateDayErrorTy[]
    workingCenters: TwinDictionary
}

const ErrorsByDays: React.FC<ErrorsByDaysProps> = ({ day, dayErrors, workingCenters }) => {
    let renderThis: JSX.Element[] = []
    for (const error of dayErrors) {
        renderThis.push(<ErrorRow dayError={error} workingCenters={workingCenters} key={day + error.EmployeeId + error.WorkingCenterId} />)
    }

    return (
        <div>
            <div className='medium16 mb-20 mt-10' key={day}>{day}</div>
            {renderThis}
        </div>
    )
}


interface ErrorRowProps {
    dayError: WCalModalDuplicateDayErrorTy
    workingCenters: TwinDictionary
}

const ErrorRow: React.FC<ErrorRowProps> = ({ dayError, workingCenters }) => {
    const { t } = useTwinTranslation()

    const dictReasonError: TwinDictionary = {
        'sameWorkingCenterTurn': { text: t('alreadyTurnAtWorkingCenter', 'Hay un turno establecido en el centro de trabajo') },
        'sameDayTurn': { text: t('alreadyTurnForThisDay', 'Hay un turno establecido para este día') },
        'holidayDay': { text: t('IsAHolidayDay', 'Es un día de vacaciones') },
    }
    const workingCenterName = dayError.WorkingCenterId ? workingCenters?.[dayError.WorkingCenterId]?.name : ''
    return (
        <div className='mb-10'>
            <span className='medium14'>{dayError.employeeName}</span>
            <span> - </span>
            <span>{dictReasonError[dayError.reason].text}</span>
            {workingCenterName ? <span className='ml-5'>{workingCenterName}</span> : null}
        </div>
    )
}

export default ModalDuplicateWCalTurn